import React, { useEffect, useState } from 'react';
import api from '../../services/api';

// Reads classes / sections / subjects from the tenant's school_config record so
// every Education-tenant page picks from the same source of truth.
//
// Cache strategy:
//   - In-memory cache for fast subsequent renders.
//   - 30s TTL so updates from School Configuration propagate without a hard reload.
//   - `invalidateClassContextCache()` lets School Configuration force a refresh
//     on save (immediate consistency for the user who just edited it).
//   - `force=true` arg bypasses the cache entirely.
let _cache = null;
let _cacheAt = 0;
let _cachePromise = null;
const CACHE_TTL_MS = 30 * 1000;

async function loadSchoolConfig(force = false) {
  const fresh = !force && _cache && (Date.now() - _cacheAt) < CACHE_TTL_MS;
  if (fresh) return _cache;
  if (_cachePromise) return _cachePromise;
  _cachePromise = (async () => {
    try {
      const r = await api.get('/records/school_config', { params: { limit: 1 } });
      const rd = (r.data?.records || [])[0]?.recordData || {};
      _cache = {
        classes:  Array.isArray(rd.classes)  ? rd.classes  : [],
        sections: Array.isArray(rd.sections) ? rd.sections : [],
        subjects: Array.isArray(rd.subjects) ? rd.subjects : [],
      };
      _cacheAt = Date.now();
      return _cache;
    } catch {
      _cache = { classes: [], sections: [], subjects: [] };
      _cacheAt = Date.now();
      return _cache;
    } finally { _cachePromise = null; }
  })();
  return _cachePromise;
}
export const invalidateClassContextCache = () => { _cache = null; _cacheAt = 0; _cachePromise = null; };
// Allow consumers to manually refresh — useful after School Config edits.
export const refreshSchoolConfig = () => loadSchoolConfig(true);

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/**
 * Reusable filter row for "which class / section / subject / day / time".
 *
 * Props:
 *   value      — { classGrade, section, subject, day, time }
 *   onChange   — (next) => void
 *   fields     — array subset of ['classGrade','section','subject','day','time'] (default = all five)
 *   required   — array subset of fields that must have a value (visual cue only)
 *   compact    — render as small inputs in one row
 *   className  — extra wrapper classes
 */
export default function ClassContextPicker({
  value = {},
  onChange,
  fields = ['classGrade', 'section', 'subject'],
  required = [],
  compact = false,
  className = '',
}) {
  const [config, setConfig] = useState({ classes: [], sections: [], subjects: [] });

  useEffect(() => {
    loadSchoolConfig().then(setConfig);
  }, []);

  const set = (k, v) => onChange({ ...value, [k]: v });
  const isReq = (k) => required.includes(k);

  const colCls = compact ? 'col-md-2 col-sm-6' : 'col-md-3 col-sm-6';
  const sizeCls = compact ? 'form-select-sm' : '';
  const inputSizeCls = compact ? 'form-control-sm' : '';

  // Always render a dropdown with the configured options + an "Other (enter
  // manually)" choice at the bottom. If the current value isn't in the list
  // (because school config is empty or the user typed something custom
  // earlier), the picker enters "other mode" automatically.
  //
  // We track "other mode" purely in local state so we never push a sentinel
  // value onto the consumer — only real strings.
  const OTHER = '__other__';
  const placeholders = { classGrade: 'e.g. Class 10', section: 'A', subject: 'Mathematics' };
  // For filter use ("All classes") vs. forms ("Select class…") we adapt the
  // empty-option label based on whether the field is `required`.
  const allLabels    = { classGrade: 'All classes',  section: 'All sections',  subject: 'All subjects' };
  const selectLabels = { classGrade: 'Select class…', section: 'Select section…', subject: 'Select subject…' };
  const fieldLabels  = { classGrade: 'Class / Grade', section: 'Section', subject: 'Subject' };

  // Per-field "is the user in Other mode?" — independent of the actual value
  // so that picking Other before typing anything keeps the input visible.
  const [otherMode, setOtherMode] = useState({});
  const enableOther  = (key) => setOtherMode(p => ({ ...p, [key]: true }));
  const disableOther = (key) => setOtherMode(p => ({ ...p, [key]: false }));

  const renderSelect = (key, list) => {
    const v          = value[key] || '';
    const inList     = !!v && list.includes(v);
    // Auto-enable Other if the value isn't in the list (e.g. legacy data).
    const showOther  = otherMode[key] || (!!v && !inList);
    const selectV    = showOther ? OTHER : (inList ? v : '');

    const onSelect = (e) => {
      const next = e.target.value;
      if (next === OTHER) {
        enableOther(key);
        // Clear value so the input starts empty unless user is editing existing.
        if (inList) set(key, '');
      } else {
        disableOther(key);
        set(key, next);
      }
    };

    return (
      <div className={colCls}>
        <label className="form-label small mb-1">
          {fieldLabels[key]}{isReq(key) && <span className="text-danger">*</span>}
        </label>
        <select className={`form-select ${sizeCls}`} value={selectV} onChange={onSelect}>
          <option value="">{isReq(key) ? selectLabels[key] : allLabels[key]}</option>
          {list.map(item => <option key={item} value={item}>{item}</option>)}
          <option value={OTHER}>Other (enter manually) ↓</option>
        </select>
        {showOther && (
          <input
            className={`form-control ${inputSizeCls} mt-1`}
            placeholder={`Type ${fieldLabels[key].toLowerCase()} — ${placeholders[key]}`}
            value={inList ? '' : v}
            autoFocus
            onChange={e => set(key, e.target.value)}
          />
        )}
        {/* If the school config has no values for this field, prompt admins to
            configure them — otherwise users only see "Other" and don't realize
            the dropdown was supposed to be pre-populated. */}
        {list.length === 0 && (
          <div className="text-muted mt-1" style={{ fontSize: 11 }}>
            <i className="bi bi-info-circle me-1"/>
            No {key === 'classGrade' ? 'classes' : key === 'section' ? 'sections' : 'subjects'} configured.
            Add them in <strong>School Configuration</strong> for a populated dropdown.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`row g-2 ${className}`}>
      {fields.includes('classGrade') && renderSelect('classGrade', config.classes)}
      {fields.includes('section')    && renderSelect('section',    config.sections)}
      {fields.includes('subject')    && renderSelect('subject',    config.subjects)}

      {fields.includes('day') && (
        <div className={colCls}>
          <label className="form-label small mb-1">Day {isReq('day') && <span className="text-danger">*</span>}</label>
          <select className={`form-select ${sizeCls}`} value={value.day || ''} onChange={e => set('day', e.target.value)}>
            <option value="">Any day</option>
            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      {fields.includes('time') && (
        <div className={colCls}>
          <label className="form-label small mb-1">Time {isReq('time') && <span className="text-danger">*</span>}</label>
          <input type="time" className={`form-control ${inputSizeCls}`} value={value.time || ''} onChange={e => set('time', e.target.value)} />
        </div>
      )}
    </div>
  );
}

// Helper hook: load school config + expose helpers for consumers that don't
// need the full picker UI (e.g., one-off dropdowns).
export function useSchoolConfig() {
  const [cfg, setCfg] = useState({ classes: [], sections: [], subjects: [] });
  useEffect(() => { loadSchoolConfig().then(setCfg); }, []);
  return cfg;
}
